import Cookies from "js-cookie";
import type { Theme } from "@/providers/theme/ThemeContext";

/** Cookie name — must stay in sync with the bootstrap script in index.html. */
export const THEME_COOKIE_NAME = "theme";

export const DEFAULT_THEME: Theme = "light";

const THEME_COOKIE_MAX_AGE_DAYS = 365;

const THEME_COOKIE_PATTERN = new RegExp(
  `(?:^|;\\s*)${THEME_COOKIE_NAME}=(light|dark)(?:;|$)`
);

export const isValidTheme = (value: string | undefined | null): value is Theme =>
  value === "light" || value === "dark";

/** Parse theme from a raw `document.cookie` string (bootstrap script parity). */
export const parseThemeFromCookieString = (
  cookieString: string
): Theme | null => {
  const match = cookieString.match(THEME_COOKIE_PATTERN);
  return match && isValidTheme(match[1]) ? match[1] : null;
};

export const getStoredTheme = (): Theme | null => {
  const value = Cookies.get(THEME_COOKIE_NAME);
  return isValidTheme(value) ? value : null;
};

export const resolveTheme = (
  stored: Theme | null | undefined,
  cached: Theme | null | undefined
): Theme => {
  if (stored) {
    return stored;
  }
  if (cached && isValidTheme(cached)) {
    return cached;
  }
  return DEFAULT_THEME;
};

export const setStoredTheme = (theme: Theme): void => {
  Cookies.set(THEME_COOKIE_NAME, theme, {
    expires: THEME_COOKIE_MAX_AGE_DAYS,
    path: "/",
    sameSite: "Lax",
  });
};

export const applyThemeToBody = (theme: Theme): void => {
  const body = document.getElementById("body");
  if (!body) {
    return;
  }
  if (theme === "dark") {
    body.classList.add("dark");
  } else {
    body.classList.remove("dark");
  }
};
